import { SnowflakeSettings } from 'app/model/dataflow/dataset-settings/snowflake-settings';
import { TabularTypeAdapter } from './tabular-type.adapter';
import { Metamodel } from 'app/model/base/model';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { Owned } from 'app/model/base/named';
import { Dataset, DataFlowDSLAdapterRegistry, LinkedService } from 'shell-models-lib';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowAdapterUtils } from '../adapter.util';
import { DatasetUtils, getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(SnowflakeSettings)
})
export class SnowflakeTableAdapter extends TabularTypeAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean, isInline: boolean, _datasetSettings: DataFlowDatasetSettings | undefined, format?: string): void {
        if (linkedService) {
            const dbName = DatasetUtils.getDatabaseFromLinkedService(linkedService, 'database');
            if (dbName) {
                props.push(`database: ${DataFlowAdapterUtils.formatExpressionValue(dbName)}`);
            }
        }

        if (isInline || !dataset) {
            return;
        }

        props.push(`store: '${DatasetFormatType.Snowflake}'`);

        // For V2 linked services, push the V2 store as well 
        if (linkedService && Metamodel.modelOf(linkedService).name === adfModelTypeMap.snowflakeV2.linkedServiceType) {
            props.push(`store: '${DatasetFormatType.SnowflakeV2}'`);
        }

        // Only add schema and table for 'table' format (not for 'query')
        if (!isSource || format === 'table') {
            const schema = getAttributeValueFromDataset(dataset, 'schema');
            if (schema) {
                props.push(`schemaName: ${DataFlowAdapterUtils.formatExpressionValue(schema)}`);
            }

            const tableName = getAttributeValueFromDataset(dataset, 'table') || getAttributeValueFromDataset(dataset, 'tableName');
            if (tableName) {
                props.push(`tableName: ${DataFlowAdapterUtils.formatExpressionValue(tableName)}`);
            }
        }
    }

    public async toSourceDSL(setting: SnowflakeSettings): Promise<string[]> {
        const props = await super.toSourceDSL(setting);
        props.push(`store: 'snowflake'`);
        return props;
    }

    public async toSinkDSL(setting: SnowflakeSettings): Promise<string[]> {
        const props = await super.toSinkDSL(setting);
        props.push(`store: 'snowflake'`);
        return props;
    }

    public async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<SnowflakeSettings> {
        return (await super.toSourceModel(owner, configs, handledConfigs)) as SnowflakeSettings;
    }

    public async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<SnowflakeSettings> {
        const setting = (await super.toSinkModel(owner, configs, handledConfigs)) as SnowflakeSettings;
        if (configs.has('stageInsert') && configs.get('stageInsert') === 'true') {
            owner.snowflakeStageInsert = true;
            handledConfigs.push('stageInsert');
        }
        return setting;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): SnowflakeSettings {
        const result = Owned.create<SnowflakeSettings>(SnowflakeSettings.MODEL, owner);
        result.initialize(undefined);
        return result;
    }
}
